import Vue from 'vue'
import mainAPI from './mainAPI' 

const outOfDatesAndMarket = (currentMarket, promotion) => {
      const now = new Date();
        // Check if active
        if (!promotion.isActive) {
          return false;
        }

        // Check start date
        if (promotion.startDate) {
          const startDate = new Date(promotion.startDate);
          if (now < startDate) return false;
        }
        
        // Check end date
        if (promotion.endDate) {
          const endDate = new Date(promotion.endDate);
          if (now > endDate) return false;
        }
        
        // Check market restrictions
        if (currentMarket && promotion.markets && promotion.markets.length) {
          if (!promotion.markets.includes('*') && !promotion.markets.includes(currentMarket)) {
            return false;
          }
        }
        
        return true;
      };

export default {
  namespaced: true,
  
  state: () => ({
    promotions: [],              // All promotions from API
    activePromotions: [],        // Filtered promotions that are currently active
    loadingPromotions: false,    // Loading state
    promotionsError: null,       // Error tracking
    appliedPromotions: {},       // Applied promotions
    hasLoaded: false,            // has promotions json loaded?
    selectedGiftOption: null,     // if a gift is selected
    showGiftChoicePopup: false   // popup for router guard
  }),
  
  getters: {
    allPromotions: state => state.promotions,

    multipleChoicePromo: state => {
      const activePromos = state.activePromotions || [];
      
      // Find the highest weight promo
      const highestWeight = Math.max(0, ...activePromos.map(p => p.weight || 0));
      
      const topMultiplePromo = activePromos.find(p => 
        p.promoType === 'multiple' && (p.weight || 0) === highestWeight
      );
      
      return topMultiplePromo || null;
    },

    // Check if multiple choice promotion is applied
    hasMultipleChoicePromo: state => {
        const promo = state.appliedPromotions
        return promo && 
            Object.keys(promo).length > 0 && 
            Object.values(promo).some(promo => promo.promoType === 'multiple')
    },
    
    // Get UI elements for multiple choice promotions
    getUiElements: (state) => (promoType, productKey, elementKey) => {
      const promotion = state.activePromotions.find(p => p.promoType === promoType);
      if (!promotion) return null;
      
      const product = promotion.products.find(p => p.productKey === productKey);
      if (!product) return null;
      
      return product.ui_elements.find(ui => ui.key === elementKey);
    }
  },
  
  mutations: {
    SET_SELECTED_GIFT_OPTION(state, optionKey) {
      state.selectedGiftOption = optionKey;
    },

    SET_SHOW_GIFTCHOICE_POPUP(state, showPopup) {
      state.showGiftChoicePopup = showPopup
    },

    SET_PROMOTIONS(state, promotions) {
      state.promotions = promotions;
    },
    
    SET_ACTIVE_PROMOTIONS(state, promotions) {
      state.activePromotions = promotions;
    },
    
    SET_LOADING_PROMOTIONS(state, isLoading) {
      state.loadingPromotions = isLoading;
    },
    
    SET_PROMOTIONS_ERROR(state, error) {
      state.promotionsError = error;
    },
    
    APPLY_PROMOTION(state, { productKey, promotion, product, triggerSource }) {
      // Clear any existing applied promotions
      state.appliedPromotions = {};
      
      Vue.set(state.appliedPromotions, promotion.key, {
        key: promotion.key,
        name: promotion.name,
        promoType: promotion.promoType || "single",
        giftOptions: promotion.gift_options || null,
        WP1Code: promotion.WP1Code,
        ui_elements: promotion.ui_elements,
        triggerSource: triggerSource,
        product: {
          productKey: product.productKey,
          productName: product.productName,
          speed: product.speed,
          ui_elements: product.ui_elements
        }
      });
    },

    CLEAR_APPLIED_PROMOTIONS(state) {
      state.appliedPromotions = {};
    },

    SET_HAS_LOADED(state, value) {
      state.hasLoaded = value;
    }
    
  },
  
  actions: {
    //set popup visibility
    setGiftChoicePopup({ commit, state }, showPopup) {
      commit('SET_SHOW_GIFTCHOICE_POPUP', showPopup);
    },

    //Select gift option for multiple promo type
    setSelectedGiftOption({ commit, state }, optionKey) {
      commit('SET_SELECTED_GIFT_OPTION', optionKey);
      
      // Return the promise
      return Promise.resolve(optionKey);
    },
    // Load promotions from API
    fetchPromotions({ commit, dispatch, rootState }) {
      commit('SET_LOADING_PROMOTIONS', true);
      commit('SET_PROMOTIONS_ERROR', null);
      
      return mainAPI._fetchPromotions()
        .then(response => {
          const promotions = response.promotions || [];
          commit('SET_PROMOTIONS', promotions);
          commit('SET_HAS_LOADED', true);
          dispatch('filterActivePromotions');
          return promotions;
        })
        .catch(error => {
          console.error('Error fetching promotions:', error);
          commit('SET_PROMOTIONS_ERROR', error);
        })
        .finally(() => {
          commit('SET_LOADING_PROMOTIONS', false);
        });
    },
    
    // Filter active promotions based on current date, market, and trigger
    filterActivePromotions({ state, commit, rootState }) {
      const now = new Date();
      const currentMarket = rootState.meta.marketType;
      
      const activePromotions = state.promotions.filter((promotion) => outOfDatesAndMarket(currentMarket, promotion)).filter(promotion => {
        
        // Always include promotions with siteWide flag
        if (promotion.triggers && promotion.triggers.some(trigger => trigger.siteWide === true)) {
          return true;
        }
        
        // Exclude promotions with specific triggers that should be activated explicitly
        if (promotion.triggers && 
            (promotion.triggers.some(trigger => trigger.queryString !== null && trigger.queryString !== '') ||
             promotion.triggers.some(trigger => trigger.pageSlug === true) || 
             promotion.triggers.some(trigger => trigger.AbandonedCart === true))) {
          return false;
        }


        // Check if siteWide set to false
        if (promotion.triggers && promotion.triggers.some(trigger => trigger.siteWide === false)) {
          return false;
        }
        
        return true;
      });

      commit('SET_ACTIVE_PROMOTIONS', activePromotions);
    },

    // Refilters promotions after a user addresses in
    refilterPromotions({ state, dispatch, rootState }) {
      if (state.promotions.length > 0 && rootState.meta.marketType) {
        dispatch('filterActivePromotions');
        dispatch('checkForPromotions');
        console.log('✅ Refiltered Promotions')

        return true;
      }
      return false;
    },
    applyPromotion({ state, commit, dispatch }, { offer, triggerSource }) {
        // Don't proceed if no download speed or no active promotions
        if (!offer?.download || !state.activePromotions.length) {
            commit('CLEAR_APPLIED_PROMOTIONS');
            return null;
        }
        
        const downloadSpeed = offer.download;
          
        // First, try to find multiple choice promo that matches the speed
        let multiplePromo = state.activePromotions.find(promo => 
            promo.promoType === 'multiple' && 
            promo.products && 
            promo.products.some(product => product.speed === downloadSpeed)
        );
      
        // If found, apply it
        if (multiplePromo) {
            const product = multiplePromo.products.find(p => p.speed === downloadSpeed);
            
            if (product) {
                const promoTriggerSource = multiplePromo.triggerSource || triggerSource || 'unknown';
                
                // Apply the multiple choice promotion
                commit('APPLY_PROMOTION', {
                    productKey: product.productKey,
                    promotion: multiplePromo,
                    product: product,
                    triggerSource: promoTriggerSource
                });
                
                return;
            }
        }
        
        // If no multiple choice promo, fall back to regular promo matching
        const matchingPromotions = state.activePromotions.filter(promo => 
            promo.products && promo.products.some(product => product.speed === downloadSpeed)
        );
  
        // If no matching promotions, clear any existing promotions
        if (matchingPromotions.length === 0) {
            commit('CLEAR_APPLIED_PROMOTIONS');
            return null;
        }
          
        // Sort by weight (higher weight is higher priority)
        const sortPromotions = matchingPromotions.sort((a, b) => (b.weight || 0) - (a.weight || 0));
      
        // Find highest priority promotion
        if (sortPromotions.length > 0) {
            const topPromotion = sortPromotions[0];
            const product = topPromotion.products.find(p => p.speed === downloadSpeed);
          
            if (product) {
                const promoTriggerSource = topPromotion.triggerSource || triggerSource || 'unknown';
    
                // Apply Promotion
                commit('APPLY_PROMOTION', { 
                    productKey: product.productKey, 
                    promotion: topPromotion,
                    product: product,
                    triggerSource: promoTriggerSource
                });
    
                if (topPromotion.WP1Code) {
                  dispatch('Buyflow/addPromotion', topPromotion.WP1Code, { root: true });
                }
            }
        }
    },
    
    initialize({ dispatch, state }) {
      // Only fetch promotions if they haven't been loaded yet
      if (!state.hasLoaded) {
        return dispatch('fetchPromotions')
          .then(() => {
            return dispatch('filterActivePromotions');
          })
          .then(() => {
            return dispatch('checkForPromotions');
          });
      }
      // return existing promotions
      return Promise.resolve(state.promotions);
    },

    /*
    * Check page for promotions
    */ 
    checkForPromotions({ commit, state, rootState }) {
      let newPromotions = [];
      let triggerSources = {};
      const currentMarket = rootState.meta.marketType;

       // Filter all promotions by market
      const activePromotions = state.promotions.filter((promotion) => outOfDatesAndMarket(currentMarket, promotion));

      // Check URL parameter promotions
      const promoWithQueryStringTrigger = activePromotions.filter(promo => 
          promo.triggers && 
          promo.triggers.find(trigger => trigger.queryString)
      );
      if (promoWithQueryStringTrigger.length > 0) {
        const queryParams = new URLSearchParams(window.location.search);
        promoWithQueryStringTrigger.forEach(promo => {
          const promoQs = (promo.triggers.find(trigger => trigger.queryString && trigger.queryString !== null && trigger.queryString!=='') || {queryString:null}).queryString;
          if (promoQs) {
              const promoCode = queryParams.get(promoQs);
              if (promoCode && promoCode !== null && promoCode !== '') {
                  newPromotions.push(promo);
                  triggerSources[promo.key] = 'queryString';
              }
          }
        });
      }

      // Check for abandoned cart promotions
      const isAbandonedCart = rootState.meta?.isAbandonedCart || false;

      if (isAbandonedCart) {
        const abandonedCartPromos = activePromotions.filter(promo => 
          promo.triggers && 
          promo.triggers.some(trigger => trigger.AbandonedCart === true)
        );
        
        if (abandonedCartPromos.length) {
          abandonedCartPromos.forEach(promo => {
            newPromotions.push(promo);
            triggerSources[promo.key] = 'AbandonedCart';
          });
        }
      }

      const currentPath = window.location.pathname;
      const pageSlugPromos = activePromotions.filter(promo => 
        promo.triggers && 
        promo.triggers.some(trigger => currentPath === trigger.pageSlug)
      );
      if (pageSlugPromos.length) {
        pageSlugPromos.forEach(promo => {
          newPromotions.push(promo);
          triggerSources[promo.key] = 'pageSlug';
        });
      }

      // Filter promotions
      if (newPromotions.length > 0) {
        // Check if there are any special trigger promotions (abandonedCart, queryString, pageSlug)
        const triggeredPromo = newPromotions.length > 0;
        
        let updatedPromotions = [];
        
        if (triggeredPromo) {
          // Get existing site-wide promotions
          const siteWidePromos = state.activePromotions.filter(promo => 
            promo.triggers && 
            promo.triggers.some(trigger => trigger.siteWide === true) &&
            promo.promoType !== 'multiple'
          );
          
          // Add site-wide promos (except multiple type) to updated list
          updatedPromotions = [...siteWidePromos];
          
          // Add new promotions
          newPromotions.forEach(newPromo => {
            // Add triggerSource
            updatedPromotions.push({
              ...newPromo,
              triggerSource: triggerSources[newPromo.key]
            });
          });
        } else {
          // If no special trigger promos, use all current active promotions
          updatedPromotions = [...state.activePromotions];
        }
        // Update active promotions
        commit('SET_ACTIVE_PROMOTIONS', updatedPromotions);
      }
      
      return [];
    }
  }
};